import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { DirectionsCar as CarIcon } from '@mui/icons-material';
import type { Technika } from '../api/JsdhNotifikaceService/generated/models/Technika';
import type { PoplachTechnikaResponse } from '../api/JsdhNotifikaceService/generated/models/PoplachTechnika';
import { splitEmailText } from '../utils/technikaParser';
import TechnikaFoto from './TechnikaFoto';

// Karta může být buď spárovaná s DB (s plnými daty) nebo unmatched z emailu (pouze text).
// Obě varianty se renderují stejným layoutem; unmatched nemá foto/SPZ/poznámku, jen
// volací znak + název + KOPIS chip + placeholder ikonu auta.
export type TechnikaCard =
    | { type: 'matched'; technika: Technika; source?: PoplachTechnikaResponse }
    | { type: 'unmatched'; text: string };

interface Vyjezd2026TechnikaSectionProps {
    cards: TechnikaCard[];
    deviceToken?: string;
}

const MAX_TECHNIKA = 6;

// Layout dle počtu karet: 1-3 jedna řada, 4 jako 2×2, 5-6 jako 3×2.
// Pro 1 vůz: pevná šířka 50% (nestretchovat na celou sekci — vypadá to bizarně s velkou prázdnou plochou).
const cardFlexBasis = (count: number): string => {
    if (count <= 1) return 'calc(50% - 4px)';
    if (count === 2) return 'calc(50% - 4px)';
    if (count === 3) return 'calc(33.333% - 6px)';
    if (count === 4) return 'calc(50% - 4px)';
    return 'calc(33.333% - 6px)'; // 5 nebo 6
};

// Pro 1 vůz nedovolíme kartu rozšířit přes flexGrow — drží basis (50%).
// Pro 2+ vozů povolíme grow (vyplní řádek rovnoměrně).
const cardFlexGrow = (count: number): number => (count <= 1 ? 0 : 1);

// Font volacího znaku — odvozeno od šířky karty.
// Pro 1 vůz je karta užší (50%), takže styl 2-card (2rem volací znak).
const volaciFontSize = (count: number): string => {
    if (count <= 1) return '2rem';
    if (count === 2) return '2rem';
    if (count === 3) return '1.5rem';
    if (count === 4) return '1.8rem';
    return '1.5rem'; // 5-6
};

const nazevFontSize = (count: number): string => {
    if (count <= 1) return '1.1rem';
    if (count === 2) return '1.1rem';
    if (count === 3) return '0.9rem';
    if (count === 4) return '1rem';
    return '0.9rem';
};

export const Vyjezd2026TechnikaSection: React.FC<Vyjezd2026TechnikaSectionProps> = ({
    cards, deviceToken,
}) => {
    const visible = cards.slice(0, MAX_TECHNIKA);
    const overflow = Math.max(0, cards.length - MAX_TECHNIKA);
    const visibleCount = visible.length;
    const basis = cardFlexBasis(visibleCount);

    if (visibleCount === 0) {
        return (
            <Box sx={{
                flex: 1, p: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 1,
            }}>
                <CarIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'text.secondary' }}>
                    Žádná technika nepovolána
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ flex: 1, p: 1, display: 'flex', flexDirection: 'column', gap: 0.5, minHeight: 0 }}>
            {overflow > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled', fontStyle: 'italic' }}>
                        + {overflow} dalších
                    </Typography>
                </Box>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignContent: 'stretch', flex: 1, minHeight: 0 }}>
                {visible.map((card, idx) => {
                    if (card.type === 'matched') {
                        const t = card.technika;
                        const source = card.source;
                        const poznamka = source?.zdroj === 'VELITEL' ? source.poznamka?.trim() : '';
                        return (
                            <Paper key={`m-${t.id}`} elevation={1} sx={{
                                p: 0.75, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.5,
                                flexBasis: basis, flexGrow: cardFlexGrow(visibleCount), flexShrink: 1, minWidth: 0, minHeight: 0, overflow: 'hidden',
                                position: 'relative',
                            }}>
                                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                                    <Typography sx={{
                                        fontSize: volaciFontSize(visibleCount),
                                        fontWeight: 900, color: 'error.main', lineHeight: 1.1,
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }}>
                                        {t.volaciZnak}
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: nazevFontSize(visibleCount),
                                        fontWeight: 600, color: 'text.primary', lineHeight: 1.3, wordBreak: 'break-word',
                                    }}>
                                        {t.nazev}
                                    </Typography>
                                    {t.spz && (
                                        <Typography sx={{
                                            fontSize: visibleCount >= 3 ? '0.7rem' : '0.85rem',
                                            color: 'text.disabled', lineHeight: 1.3, mt: 0.5,
                                        }}>
                                            {t.spz}
                                        </Typography>
                                    )}
                                    {source && (
                                        <Chip
                                            size="small"
                                            label={
                                                source.zdroj === 'KOPIS'
                                                    ? 'KOPIS'
                                                    : source.potvrzenoKopis
                                                        ? 'Velitel + KOPIS'
                                                        : 'Velitel'
                                            }
                                            sx={{
                                                mt: 0.5, fontWeight: 700,
                                                fontSize: visibleCount >= 3 ? '0.55rem' : '0.7rem',
                                                color: '#fff',
                                                bgcolor: source.zdroj === 'KOPIS' ? 'info.main' : source.potvrzenoKopis ? 'success.main' : 'warning.main',
                                            }}
                                        />
                                    )}
                                </Box>
                                <TechnikaFoto
                                    technikaId={t.id}
                                    maFotografii={t.maFotografii}
                                    alt={t.nazev}
                                    deviceToken={deviceToken}
                                    objectFit="cover"
                                    ikonaFontSize={visibleCount >= 3 ? 64 : 96}
                                    sx={{ height: '100%', aspectRatio: '4/3', maxWidth: '70%', minWidth: 0 }}
                                />
                                {/* Poznámka velitele — překryv přes celou šířku karty u spodního okraje
                                    (přes obrázek i text). Kontrastní červené pozadí, bílé tučné písmo. */}
                                {poznamka && (
                                    <Box sx={{
                                        position: 'absolute', left: 0, right: 0, bottom: 0,
                                        bgcolor: 'error.main', color: '#fff',
                                        px: 1, py: 0.5,
                                        fontSize: visibleCount >= 3 ? '0.7rem' : '0.85rem',
                                        fontWeight: 700, lineHeight: 1.3,
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }} title={poznamka}>
                                        {poznamka}
                                    </Box>
                                )}
                            </Paper>
                        );
                    }
                    // unmatched — fallback karta s daty z emailu, vizuálně shodná s matched KOPIS.
                    // Bez tooltipu (kiosek bez myši), bez foto/SPZ/poznámky, žádné výstražné prvky.
                    const { volaciZnak, nazev } = splitEmailText(card.text);
                    return (
                        <Paper key={`u-${idx}`} elevation={1} sx={{
                            p: 0.75, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.5,
                            flexBasis: basis, flexGrow: cardFlexGrow(visibleCount), flexShrink: 1, minWidth: 0, minHeight: 0, overflow: 'hidden',
                            position: 'relative',
                        }}>
                            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                                <Typography sx={{
                                    fontSize: volaciFontSize(visibleCount),
                                    fontWeight: 900, color: 'error.main', lineHeight: 1.1,
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}>
                                    {volaciZnak}
                                </Typography>
                                {nazev && (
                                    <Typography sx={{
                                        fontSize: nazevFontSize(visibleCount),
                                        fontWeight: 600, color: 'text.primary', lineHeight: 1.3, wordBreak: 'break-word',
                                    }}>
                                        {nazev}
                                    </Typography>
                                )}
                                <Chip
                                    size="small"
                                    label="KOPIS"
                                    sx={{
                                        mt: 0.5, fontWeight: 700,
                                        fontSize: visibleCount >= 3 ? '0.55rem' : '0.7rem',
                                        color: '#fff', bgcolor: 'info.main',
                                    }}
                                />
                            </Box>
                            <Box sx={{
                                height: '100%', aspectRatio: '4/3', maxWidth: '70%', minWidth: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: 'action.hover', borderRadius: 1, flex: '0 0 auto',
                            }}>
                                <CarIcon sx={{ fontSize: visibleCount >= 3 ? 64 : 96, color: 'text.disabled' }} />
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        </Box>
    );
};
